import React, { ReactElement } from 'react'
import { CreditCardListModel } from 'src/models/creditCard.models'

import CardSettingsTile, {
  CountryOption,
  UpdatedBillingDetails,
} from './components/CardSettingsTile/CardSettingsTile'
import CardStatusLifecycleTile from './components/CardStatusLifecycleTile/CardStatusLifecycleTile'
import SpendLimitTile from './components/SpendLimitTile/SpendLimitTile'
import SpendRestrictionsTile from './components/SpendRestrictionsTile/SpendRestrictionsTile'
import style from './UpdatedPhysicalCardSettings.style'

export type { CountryOption, UpdatedBillingDetails }

type UpdatedPhysicalCardSettingsProps = {
  creditCard: CreditCardListModel
  /** Localized country list, resolved by the page. */
  countryOptions: CountryOption[]
  /** Read-only phone shown on the billing form. Omitted when unknown. */
  contactPhone?: string
  /** `canUpdateCreditCard` — false renders the billing disclosure read-only. */
  canEditCardDetails: boolean
  /** `canDestroy` — gates the Admin/Owner-only Cancel card lifecycle row. */
  canCancelCard: boolean
  /**
   * Admin/Owner. Merchant categories are a stated view-only guardrail for Card
   * Managers, so false hides the drawer's edit affordance.
   */
  canEditMerchantCategories: boolean
  /**
   * Admin/Owner. Increases from anyone else are routed for approval, so the
   * spend-limit form asks them to submit a request rather than apply a change.
   */
  canApplySpendLimitDirectly: boolean
  /** `PHYSICAL_CARD_TRANSACTIONS_FEED` — required to connect the feed. */
  canConnectPhysicalCardFeed: boolean
  /** Brand gate for the connected-capabilities disclosure. */
  showConnectedCapabilities: boolean
  /** Persists the Card settings tile's billing address + card name. */
  onSaveBillingDetails?: (details: UpdatedBillingDetails) => void
  isSavingBillingDetails?: boolean
  billingDetailsSaveError?: boolean
  onPhysicalCardFeedConnected?: () => void
  onPhysicalCardFeedDisconnected?: () => void
}

/**
 * Redesigned physical card Account Settings surface (EX-40354) — a bento of
 * control tiles. Props-only: the consuming page owns the flag, the
 * permission/entitlement/brand gates, data loading and every write.
 */
export default function UpdatedPhysicalCardSettings(
  props: UpdatedPhysicalCardSettingsProps,
): ReactElement {
  const {
    creditCard,
    countryOptions,
    contactPhone,
    canEditCardDetails,
    canCancelCard,
    canEditMerchantCategories,
    canApplySpendLimitDirectly,
    canConnectPhysicalCardFeed,
    showConnectedCapabilities,
    onSaveBillingDetails,
    isSavingBillingDetails,
    billingDetailsSaveError,
    onPhysicalCardFeedConnected,
    onPhysicalCardFeedDisconnected,
  } = props

  return (
    <div className="UpdatedPhysicalCardSettings" css={style}>
      <CardStatusLifecycleTile
        creditCard={creditCard}
        canCancelCard={canCancelCard}
      />
      <SpendLimitTile
        creditCard={creditCard}
        canApplyDirectly={canApplySpendLimitDirectly}
      />
      <SpendRestrictionsTile
        canEditMerchantCategories={canEditMerchantCategories}
      />
      <CardSettingsTile
        creditCard={creditCard}
        countryOptions={countryOptions}
        contactPhone={contactPhone}
        canEditCardDetails={canEditCardDetails}
        canConnectPhysicalCardFeed={canConnectPhysicalCardFeed}
        showConnectedCapabilities={showConnectedCapabilities}
        onSaveBillingDetails={onSaveBillingDetails}
        isSavingBillingDetails={isSavingBillingDetails}
        billingDetailsSaveError={billingDetailsSaveError}
        onPhysicalCardFeedConnected={onPhysicalCardFeedConnected}
        onPhysicalCardFeedDisconnected={onPhysicalCardFeedDisconnected}
      />
    </div>
  )
}
